import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useMediaOwnerCheck } from "@/hooks/useMediaOwnerCheck";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  RefreshCw, Target, Zap, DollarSign, BarChart3, Settings, LogOut,
  Menu, X, ChevronRight, Plus, Monitor, Users, Calendar,
} from "lucide-react";
import { AdminAccessButton } from "@/components/admin/AdminAccessButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

// Import components
import { CampaignsTab } from "./ClientDashboard/components/CampaignsTab";
import { AnalyticsTab } from "./ClientDashboard/components/AnalyticsTab";
import { SettingsTab } from "./ClientDashboard/components/SettingsTab";
import { UserListingsSection } from "./ClientDashboard/components/UserListingsSection";
import { MyInfluencerBookings } from "./ClientDashboard/components/MyInfluencerBookings";
import { MySponsorshipBookings } from "./ClientDashboard/components/MySponsorshipBookings";

// Import types and utils
import { Campaign, DashboardStats } from "./ClientDashboard/types";
import { getStatusColor, getStatusText } from "./ClientDashboard/utils";

type TabKey = "overview" | "analytics" | "settings";

// ── Count-up hook ──
function useCountUp(target: number, duration = 1200, startDelay = 0) {
  const [count, setCount] = useState(0);
  const prevTarget = useRef(0);
  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    const start = prevTarget.current;
    const diff = target - start;
    const timer = setTimeout(() => {
      const startTime = performance.now();
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(start + diff * ease));
        if (progress < 1) requestAnimationFrame(tick);
        else prevTarget.current = target;
      };
      requestAnimationFrame(tick);
    }, startDelay);
    return () => clearTimeout(timer);
  }, [target, duration, startDelay]);
  return count;
}

// ── Stat Card ──
interface StatCardProps {
  label: string; rawValue: number | string; countValue?: number;
  prefix?: string; suffix?: string; icon: React.ElementType;
  gradient: string; iconColor: string; delay: string;
}
const StatCard = ({ label, rawValue, countValue, prefix = '', suffix = '', icon: Icon, gradient, iconColor, delay }: StatCardProps) => {
  const displayValue = countValue !== undefined ? `${prefix}${countValue.toLocaleString()}${suffix}` : rawValue;
  return (
    <div className="relative rounded-2xl border border-border/60 bg-card overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 animate-fade-in" style={{ animationDelay: delay }}>
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${gradient}`} />
      <div className="absolute -right-3 -top-3 opacity-[0.06]"><Icon className={`h-20 w-20 ${iconColor}`} /></div>
      <div className="p-5 relative z-10">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} inline-flex mb-3`}><Icon className="h-5 w-5 text-white" /></div>
        <p className="text-2xl font-bold text-foreground tabular-nums tracking-tight leading-none mb-1.5">{displayValue}</p>
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <div className={`mt-4 h-px bg-gradient-to-r ${gradient} opacity-20`} />
      </div>
    </div>
  );
};

const ClientDashboard = () => {
  const { language } = useLanguage();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMediaOwner, checking: checkingMediaOwner } = useMediaOwnerCheck();
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalCampaigns: 0, activeCampaigns: 0, totalSpent: 0, totalReach: 0, avgEngagement: 0
  });

  useEffect(() => { if (!user) navigate('/auth'); }, [user, navigate]);

  // ── Fetch campaigns (unchanged logic) ──
  const fetchCampaigns = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data: campaignsDataRaw, error } = await supabase
        .from('saved_campaigns').select('*, creative_urls, payment_type')
        .eq('user_id', user.id).order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching campaigns:', error);
        toast({ title: "Error", description: language === 'ar' ? 'فشل في جلب بيانات الحملات' : 'Failed to fetch campaigns data', variant: "destructive" });
        return;
      }

      let campaignsData = campaignsDataRaw || [];
      const now = new Date();
      const campaignsNeedingStatusFix = campaignsData.filter((c: any) => {
        if ((c?.status || '').toString() !== 'completed') return false;
        if (c?.is_launched) return false;
        if (Number(c?.actual_reach || 0) > 0) return false;
        if (Number(c?.actual_spent || 0) > 0) return false;
        if (!c?.end_date) return false;
        const endDate = new Date(`${c.end_date}T23:59:59`);
        return Number.isFinite(endDate.getTime()) && endDate.getTime() >= now.getTime();
      });

      if (campaignsNeedingStatusFix.length > 0) {
        const results = await Promise.all(
          campaignsNeedingStatusFix.map(async (c: any) => {
            const { error: fixError } = await supabase.from('saved_campaigns').update({ status: 'pending' }).eq('id', c.id).eq('user_id', user.id);
            if (fixError) { console.warn('Failed to auto-fix campaign status:', c.id, fixError); return { id: c.id, ok: false }; }
            return { id: c.id, ok: true };
          })
        );
        const fixedIds = new Set(results.filter(r => r.ok).map(r => r.id));
        if (fixedIds.size > 0) {
          campaignsData = campaignsData.map((c: any) => (fixedIds.has(c.id) ? { ...c, status: 'pending' } : c));
          const toastKey = 'campaignStatusAutoFixToastShown';
          if (!sessionStorage.getItem(toastKey)) {
            sessionStorage.setItem(toastKey, 'true');
            toast({ title: language === 'ar' ? 'تم تصحيح حالة الحملة' : 'Campaign status corrected', description: language === 'ar' ? 'تم تحديث حالة حملتك الجديدة إلى قيد المراجعة.' : 'Your new campaign status was updated back to Pending review.' });
          }
        }
      }

      const campaignIds = campaignsData.map((c: any) => c.id);
      let installmentsData: any[] = [];
      let approvalsData: any[] = [];

      if (campaignIds.length > 0) {
        const [installmentsResult, approvalsResult] = await Promise.all([
          supabase.from('payment_installments').select('*').in('campaign_id', campaignIds).in('status', ['pending', 'partially_paid']).order('due_date', { ascending: true }),
          supabase.from('media_owner_campaign_approvals').select('campaign_id, media_owner_name, status, rejection_reason').in('campaign_id', campaignIds)
        ]);
        installmentsData = installmentsResult.data || [];
        approvalsData = approvalsResult.data || [];
      }

      const transformedCampaigns: Campaign[] = (campaignsData || []).map((campaign) => {
        const dbStatus = campaign.status as string;
        const unpaidInstallments = installmentsData.filter(inst => inst.campaign_id === campaign.campaign_id).map(inst => ({ id: inst.id, amount: Number(inst.amount), due_date: inst.due_date, installment_number: inst.installment_number, status: inst.status }));
        return {
          id: campaign.id,
          name: campaign.campaign_name || campaign.brand_name || 'Unnamed Campaign',
          status: dbStatus,
          budget: Number(campaign.total_budget) || 0,
          spent: Number(campaign.actual_spent) || 0,
          reach: Number(campaign.actual_reach) || 0,
          engagement: Number(campaign.actual_engagement) || 0,
          startDate: campaign.start_date || '',
          endDate: campaign.end_date || '',
          locations: campaign.devices_count || (Array.isArray(campaign.selected_locations) ? campaign.selected_locations.length : 0),
          creative_urls: campaign.creative_urls || [],
          creative_metadata: Array.isArray(campaign.creative_metadata) ? (campaign.creative_metadata as Array<{ url: string; locationName?: string; mediaOwner?: string; orientation?: 'portrait' | 'landscape'; scheduledDays?: number[]; resolution?: string; }>) : [],
          payment_type: campaign.payment_type || 'immediate',
          is_archived: campaign.is_archived || false,
          rejection_reason: campaign.rejection_reason || undefined,
          unpaid_installments: unpaidInstallments,
          mediaOwnerApprovals: (() => {
            const rawOwnerName = (campaign.media_owner_name || '') as string;
            const expectedOwners = rawOwnerName.split(',').map((n: string) => n.trim()).filter((n: string) => n.length > 0);
            const fetchedForCampaign = approvalsData.filter(a => a.campaign_id === campaign.id);
            if (expectedOwners.length === 0) {
              return fetchedForCampaign.length > 0
                ? fetchedForCampaign.map(a => ({ media_owner_name: a.media_owner_name, status: a.status === 'rejected' ? 'rejected' : a.status === 'approved' ? 'approved' : 'pending', rejection_reason: a.rejection_reason || undefined }))
                : [{ media_owner_name: language === 'ar' ? 'مالك الوسائط' : 'Media Owner', status: 'pending' as string, rejection_reason: undefined }];
            }
            return expectedOwners.map((ownerName: string) => {
              const found = fetchedForCampaign.find(a => a.media_owner_name === ownerName);
              if (found) return { media_owner_name: found.media_owner_name, status: found.status === 'rejected' ? 'rejected' : found.status === 'approved' ? 'approved' : 'pending', rejection_reason: found.rejection_reason || undefined };
              return { media_owner_name: ownerName, status: 'pending' as string, rejection_reason: undefined };
            });
          })(),
          brandName: campaign.brand_name || '',
          companyName: campaign.company_name || '',
          personInCharge: campaign.person_in_charge || '',
          email: campaign.email || '',
          mobile: campaign.mobile || '',
          mediaOwnerName: campaign.media_owner_name || '',
          locationName: campaign.location_name || '',
          screenTypes: campaign.screen_types || '',
          selectedLocations: Array.isArray(campaign.selected_locations) ? (campaign.selected_locations as Array<{ id?: number; name?: string; mediaOwner?: string; region?: string; devices?: number }>) : []
        };
      });

      setCampaigns(transformedCampaigns);
      const totalCampaigns = transformedCampaigns.length;
      const activeCampaigns = transformedCampaigns.filter(c => c.status === 'approved').length;
      const totalBudget = transformedCampaigns.reduce((sum, c) => sum + c.budget, 0);
      const totalReach = transformedCampaigns.reduce((sum, c) => sum + c.reach, 0);
      const avgEngagement = transformedCampaigns.length > 0 ? transformedCampaigns.reduce((sum, c) => sum + c.engagement, 0) / transformedCampaigns.length : 0;
      setStats({ totalCampaigns, activeCampaigns, totalSpent: totalBudget, totalReach, avgEngagement });
    } catch (error) {
      console.error('Error in fetchCampaigns:', error);
      toast({ title: "Error", description: language === 'ar' ? 'حدث خطأ أثناء جلب البيانات' : 'An error occurred while fetching data', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [user, language, toast]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase.channel('campaign-changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'saved_campaigns', filter: `user_id=eq.${user.id}` }, () => {
        fetchCampaigns();
        toast({ title: language === 'ar' ? 'حملة جديدة' : 'New Campaign', description: language === 'ar' ? 'تم إنشاء حملة جديدة بنجاح' : 'A new campaign has been created successfully' });
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'saved_campaigns', filter: `user_id=eq.${user.id}` }, (payload) => {
        setCampaigns(prev => prev.map(campaign => campaign.id === payload.new.id ? { ...campaign, status: payload.new.status as string, is_archived: payload.new.is_archived || false } : campaign));
        if (payload.old.status !== payload.new.status) {
          toast({ title: language === 'ar' ? 'تحديث حالة الحملة' : 'Campaign Status Updated', description: language === 'ar' ? `تم تحديث حالة الحملة إلى: ${payload.new.status}` : `Campaign status changed to: ${payload.new.status}` });
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, language, toast, fetchCampaigns]);

  // Check for new campaign creation
  useEffect(() => {
    const checkForNewCampaign = () => {
      const campaignCreated = localStorage.getItem('campaignCreated');
      if (campaignCreated === 'true') {
        localStorage.removeItem('campaignCreated');
        localStorage.removeItem('lastCampaignId');
        fetchCampaigns();
      }
    };
    checkForNewCampaign();
    const interval = setInterval(checkForNewCampaign, 2000);
    window.addEventListener('storage', checkForNewCampaign);
    window.addEventListener('focus', fetchCampaigns);
    return () => { clearInterval(interval); window.removeEventListener('storage', checkForNewCampaign); window.removeEventListener('focus', fetchCampaigns); };
  }, [fetchCampaigns]);

  const handleLogout = useCallback(async () => { await signOut(); navigate('/'); }, [signOut, navigate]);
  const switchTab = useCallback((tab: TabKey) => { setActiveTab(tab); setSidebarOpen(false); }, []);
  const statusColorHelper = (status: string) => getStatusColor(status);
  const statusTextHelper = (status: string) => getStatusText(status, language);

  const animTotal = useCountUp(stats.totalCampaigns, 1200, 0);
  const animActive = useCountUp(stats.activeCampaigns, 1200, 80);
  const animBudget = useCountUp(stats.totalSpent, 1200, 160);

  const navItems = useMemo(() => [
    { key: "overview" as TabKey, label: "Campaigns", labelAr: "الحملات", icon: Target },
    { key: "analytics" as TabKey, label: "Analytics", labelAr: "تحليلات", icon: BarChart3 },
    { key: "settings" as TabKey, label: "Settings", labelAr: "الإعدادات", icon: Settings },
  ], []);

  const campaignTypes = [
    { id: 'dooh', icon: Monitor, title: language === 'ar' ? 'حملة DOOH' : 'DOOH Campaign', description: language === 'ar' ? 'إعلانات الشاشات الرقمية الخارجية' : 'Digital Out-of-Home advertising', route: '/campaign' },
    { id: 'influencer', icon: Users, title: language === 'ar' ? 'حملة مؤثرين' : 'Influencer Campaign', description: language === 'ar' ? 'التعاون مع المؤثرين' : 'Collaborate with influencers', route: '/influencers' },
    { id: 'event', icon: Calendar, title: language === 'ar' ? 'رعاية حدث' : 'Event Sponsorship', description: language === 'ar' ? 'رعاية الفعاليات والأحداث' : 'Sponsor events and gatherings', route: '/events-sponsors' },
  ];

  if (isMediaOwner) return <Navigate to="/media-owner-dashboard" replace />;
  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary" />
            <div className="absolute inset-0 rounded-full animate-ping border-2 border-primary/20" style={{ animationDuration: '1.5s' }} />
          </div>
          <p className="text-muted-foreground font-medium">{language === 'ar' ? 'جاري التحميل...' : 'Loading Dashboard...'}</p>
        </div>
      </div>
    );
  }

  const userInitials = (user.email?.[0] || 'C').toUpperCase();
  const isRTL = language === 'ar';

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'text-right' : 'text-left'}`}>
      <AdminAccessButton />

      {/* ── HERO HEADER ── */}
      <div className="relative overflow-hidden border-b border-border/50"
        style={{ background: 'linear-gradient(135deg, hsl(var(--primary)/0.12) 0%, hsl(var(--primary)/0.04) 50%, transparent 100%)' }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, hsl(var(--primary)), transparent)' }} />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-5" style={{ background: 'hsl(var(--primary))' }} />

        <div className="container mx-auto px-4 sm:px-6 py-6 relative z-10">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden p-2 rounded-xl bg-card border border-border shadow-sm hover:bg-accent transition-colors" onClick={() => setSidebarOpen(true)}>
                <Menu className="h-5 w-5 text-foreground" />
              </button>
              <div className="p-3 bg-primary/10 rounded-2xl border border-primary/20">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight tracking-tight">{isRTL ? 'لوحة تحكم العميل' : 'Client Dashboard'}</h1>
                <p className="text-sm text-muted-foreground">{isRTL ? 'إدارة حملاتك وتحليلات الأداء' : 'Campaign management & performance analytics'}</p>
                {user.email && (
                  <p className="text-xs text-muted-foreground/70 mt-0.5">{isRTL ? `مرحباً بعودتك 👋` : `Welcome back, ${user.email} 👋`}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border shadow-sm">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">{userInitials}</div>
                <span className="text-xs font-medium text-foreground">{isRTL ? 'عميل' : 'Client'}</span>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-xl shadow-sm">
                    <Plus className="h-4 w-4 mr-1.5" />
                    <span className="hidden sm:inline">{isRTL ? 'حملة جديدة' : 'New Campaign'}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader><DialogTitle className="text-center text-xl">{isRTL ? 'اختر نوع الحملة' : 'Choose Campaign Type'}</DialogTitle></DialogHeader>
                  <div className="grid gap-4 py-4">
                    {campaignTypes.map((type) => (
                      <button key={type.id} onClick={() => { setDialogOpen(false); navigate(type.route); }}
                        className="flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-all duration-200 text-left group">
                        <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors"><type.icon className="h-6 w-6 text-primary" /></div>
                        <div><h3 className="font-semibold text-foreground">{type.title}</h3><p className="text-sm text-muted-foreground">{type.description}</p></div>
                      </button>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
              <Button variant="outline" size="sm" onClick={fetchCampaigns} className="rounded-xl border-border/70">
                <RefreshCw className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">{isRTL ? 'تحديث' : 'Refresh'}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="rounded-xl border-border/70 hover:border-destructive/50 hover:text-destructive transition-all">
                <LogOut className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">{isRTL ? 'خروج' : 'Logout'}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="container mx-auto px-4 sm:px-6 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label={isRTL ? 'إجمالي الحملات' : 'Total Campaigns'} rawValue={stats.totalCampaigns} countValue={animTotal} icon={Target} gradient="from-blue-500 to-blue-600" iconColor="text-blue-500" delay="0ms" />
          <StatCard label={isRTL ? 'الحملات النشطة' : 'Active Campaigns'} rawValue={stats.activeCampaigns} countValue={animActive} icon={Zap} gradient="from-emerald-500 to-emerald-600" iconColor="text-emerald-500" delay="80ms" />
          <StatCard label={isRTL ? 'إجمالي الميزانية' : 'Total Budget'} rawValue={stats.totalSpent} countValue={animBudget} suffix=" SAR" icon={DollarSign} gradient="from-violet-500 to-violet-600" iconColor="text-violet-500" delay="160ms" />
        </div>
      </div>

      {/* ── LISTINGS + BOOKINGS (above main content, always visible) ── */}
      <div className="container mx-auto px-4 sm:px-6 pb-2">
        <UserListingsSection />
        <MyInfluencerBookings />
        <MySponsorshipBookings />
      </div>

      {/* ── LAYOUT: Sidebar + Content ── */}
      <div className="container mx-auto px-4 sm:px-6 pb-10">
        <div className="flex gap-6 items-start">
          {/* Desktop Sidebar */}
          <nav className="hidden lg:flex flex-col w-56 flex-shrink-0 sticky top-6">
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="p-2 space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button key={item.key} onClick={() => switchTab(item.key)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group relative
                        ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-foreground/60 rounded-r-full" />}
                      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-accent-foreground'}`} />
                      <span className="flex-1 text-left">{isRTL ? item.labelAr : item.label}</span>
                      {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary-foreground/60" />}
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-border/50 p-2 mt-1">
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all">
                  <LogOut className="h-4 w-4" /><span>{isRTL ? 'تسجيل الخروج' : 'Sign Out'}</span>
                </button>
              </div>
            </div>
          </nav>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex">
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              <div className="relative w-64 bg-card border-r border-border shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <span className="font-semibold text-foreground">{isRTL ? 'القائمة' : 'Navigation'}</span>
                  <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-accent"><X className="h-4 w-4" /></button>
                </div>
                <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.key;
                    return (
                      <button key={item.key} onClick={() => switchTab(item.key)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                          ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{isRTL ? item.labelAr : item.label}</span>
                      </button>
                    );
                  })}
                </nav>
                <div className="border-t border-border p-2">
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all">
                    <LogOut className="h-4 w-4" /><span>{isRTL ? 'تسجيل الخروج' : 'Sign Out'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Content Area */}
          <main className="flex-1 min-w-0 animate-fade-in" key={activeTab}>
            {activeTab === 'overview' && (
              <CampaignsTab campaigns={campaigns} getStatusColor={statusColorHelper} getStatusText={statusTextHelper} onRefresh={fetchCampaigns} />
            )}
            {activeTab === 'analytics' && <AnalyticsTab />}
            {activeTab === 'settings' && <SettingsTab userEmail={user.email} onSignOut={signOut} />}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
